import Sms from '@mui/icons-material/Sms'
import Button from '@mui/material/Button'
import CardActions from '@mui/material/CardActions'
import CardContent from '@mui/material/CardContent'
import { styled } from '@mui/material/styles'
import React from 'react'

import Dialog from '@/app/component/atom/dialog'
import DialogHeader from '@/app/component/atom/dialog/header'
import Select from '@/app/component/atom/form/select'
import SelectMultiple from '@/app/component/atom/form/select-multiple'
import Loading from '@/app/component/guard/loading'
import Message from '@/app/component/send/message'
import { DEFAULT_SENDER_ID_MESSAGE, DEFAULT_SENDER_ID_VALUE, SENDER_ID_PLACEHOLDER } from '@/app/definitions'
import { formatPhoneNumber } from '@/app/service/util'
import { PersonalizationType, SelectOption } from '@/app/types'

export type SenderIdOption = SelectOption<string> & { disabled?: boolean }
export type Recipient = SelectOption<string>
export type SendMessagePayload = {
  senderId: string
  isAllContacts: boolean
  contactIds: number[]
  excludeIds: number[]
  message: string
  includedGroupIds: number[]
  search?: string
}

export type Props = {
  isOpen: boolean
  contactIds: number[]
  excludeIds?: number[]
  includedGroupIds?: number[]
  isAllContacts?: boolean
  message?: string
  personalizationList?: PersonalizationType
  recipients: Recipient[]
  search?: string
  senderId?: { value: string }
  senderIds: SenderIdOption[]
  senderIdsLoading?: boolean
  totalRecipients: number
  closeHandler: () => void
  sendMessage: (payload: SendMessagePayload) => Promise<void>
}

const Send: React.FC<Props> = (props) => {
  const { senderId, senderIds } = props
  const [loading, setLoading] = React.useState(false)
  const [message, setMessage] = React.useState(props.message || '')
  const [selectedSenderId, setSelectedSenderId] = React.useState('')
  const [senderOptions, setSenderOptions] = React.useState<SenderIdOption[]>([])

  React.useEffect(() => {
    const defaults = getDefaultSenderId({ senderId, senderIds })
    setSelectedSenderId(defaults.senderId)
    setSenderOptions(defaults.senderIds)
  }, [senderId, senderIds])

  const sendHandler = async () => {
    const { contactIds, excludeIds = [], isAllContacts = false, includedGroupIds = [], search } = props
    setLoading(true)
    try {
      await props.sendMessage({
        senderId: selectedSenderId === DEFAULT_SENDER_ID_VALUE ? '' : selectedSenderId,
        isAllContacts,
        contactIds,
        excludeIds,
        message,
        includedGroupIds,
        search,
      })
    } finally {
      setLoading(false)
      props.closeHandler()
    }
  }

  const { recipients, totalRecipients, personalizationList = {}, isOpen, closeHandler } = props
  const isLoading = loading || !!props.senderIdsLoading

  const recipientsDisplay = recipients.slice(0, 4).map(({ label, value }) => ({
    label: formatPhoneNumber(`+${label}`),
    value,
  }))

  if (totalRecipients > 4) {
    recipientsDisplay.push({
      label: '...',
      value: 'more',
    })
  }

  return (
    <Dialog open={isOpen} onClose={closeHandler} fullWidth maxWidth="md">
      <DialogHeader icon={<Sms />} title="Send message" onClose={closeHandler} />
      <Loading isLoading={isLoading}>
        <form
          id="send-message-form"
          onSubmit={(e) => {
            e.preventDefault()
            if (!isLoading || message || selectedSenderId || props.recipients.length) {
              sendHandler()
            }
          }}
        >
          <CardContent>
            <SelectMultiple
              editable={false}
              name="recipients"
              label="To"
              message={
                totalRecipients !== 1
                  ? `This message will be sent to ${totalRecipients} contacts`
                  : `This message will be sent to ${totalRecipients} contact`
              }
              value={recipientsDisplay.map(({ value }) => value)}
              values={recipientsDisplay}
            />
            <Select
              editable
              id="send-message-sender"
              name="senderId"
              info={
                selectedSenderId === DEFAULT_SENDER_ID_VALUE && senderIds.length === 1 ? DEFAULT_SENDER_ID_MESSAGE : ''
              }
              label="From"
              onChange={({ value }: { value: string }) => {
                setSelectedSenderId(value)
              }}
              value={selectedSenderId}
              values={senderOptions}
            />
            <Message
              message={message}
              onChange={({ message }: { message: string }) => {
                setMessage(message)
              }}
              personalizationList={personalizationList}
            />
          </CardContent>
          <Actions>
            <ButtonContainer>
              <Button
                color="primary"
                disabled={
                  isLoading || !message || !selectedSenderId || selectedSenderId === '0' || !props.recipients.length
                }
                id="send-message-action"
                type="submit"
                variant="contained"
              >
                Send
              </Button>
            </ButtonContainer>
          </Actions>
        </form>
      </Loading>
    </Dialog>
  )
}

const Actions = styled(CardActions)(({ theme }) => ({
  position: 'absolute',
  bottom: 0,
  right: 0,
  padding: theme.spacing(3),
}))

const ButtonContainer = styled('div')({
  display: 'flex',
  width: '100%',
  justifyContent: 'flex-end',
})

const PLACEHOLDER_VALUE = '0'

export const getDefaultSenderId = ({
  senderId,
  senderIds,
}: {
  senderId?: { value: string }
  senderIds: SenderIdOption[]
}) => {
  // If there's only one sender ID, we should pre-select System Default (DEFAULT_SENDER_ID_VALUE)
  // otherwise, it's the placeholder (PLACEHOLDER_VALUE)
  let selectedSenderId = senderIds.length > 1 ? PLACEHOLDER_VALUE : DEFAULT_SENDER_ID_VALUE
  if (senderId) {
    const senderIdExists = senderIds.some((option) => option.value === senderId.value)

    selectedSenderId = senderIdExists ? senderId.value : DEFAULT_SENDER_ID_VALUE
  }

  return {
    senderId: selectedSenderId,
    senderIds: [
      {
        label: SENDER_ID_PLACEHOLDER,
        value: PLACEHOLDER_VALUE,
        disabled: true,
      },
      ...senderIds,
    ],
  }
}

export default Send
